import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { callBackendApiPost } from "../lib/api";
import { reset as userReset } from "../redux/userSlice";
import { reset as insuranceReset } from "../redux/insuranceSlice";
import { reset as stripeReset } from "../redux/stripeSlice";
import { formatDataNoPhotos } from "../lib/formatting";
import logDrtEvent from '../lib/logging';
import { alertOps } from "../lib/api";

export const useSubmit = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const insurance = useSelector((state) => state.insurance);
    const stripeInfo = useSelector((state) => state.stripe);
    const [canSubmit, setCanSubmit] = useState("");
    const [isDataRehydrated, setIsDataRehydrated] = useState(false);

    const [cardFront, setCardFront] = useState(null);
    const [cardBack, setCardBack] = useState(null);

    async function toDataURL(file) {
        return new Promise((resolve, reject) => {
            if (!(file instanceof Blob)) {
                console.warn("Invalid file object:", file);
                resolve(null);
                return;
            }

            let reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    useEffect(() => {
        async function processCardFront() {
            if (!insurance?.cardFront?.length) return;
            try {
                const dataURL = await toDataURL(insurance.cardFront[0]);
                if (dataURL) {
                    setCardFront({ dataURL, fileName: insurance.cardFront[0].name });
                }
            } catch (error) {
                console.error("Error processing card front:", error);
            }
        }
        processCardFront();
    }, [insurance?.cardFront]);

    useEffect(() => {
        async function processCardBack() {
            if (!insurance?.cardBack?.length) return;
            try {
                const dataURL = await toDataURL(insurance.cardBack[0]);
                if (dataURL) {
                    setCardBack({ dataURL, fileName: insurance.cardBack[0].name });
                }
            } catch (error) {
                console.error("Error processing card back:", error);
            }
        }
        processCardBack();
    }, [insurance?.cardBack]);

    useEffect(() => {
        setCanSubmit(!checkData() ? "fields" : "");
    }, [user]);

    useEffect(() => {
        const isStripeDataAvailable = stripeInfo && 
            (stripeInfo.paymentIntentID || stripeInfo.clientSecret);
        
        if (isStripeDataAvailable) {
            logDrtEvent('[patient_app] Redux payment data rehydrated successfully on submission', { 
                paymentIntentID: stripeInfo.paymentIntentID,
                clientSecret: stripeInfo.clientSecret ? 'exists' : 'missing',
                customerID: stripeInfo.customerID
            });
            setIsDataRehydrated(true);
        } else {
            logDrtEvent('[patient_app] Waiting for redux to rehydrate on submission', { 
                stripeInfo,
                paymentIntentID: stripeInfo?.paymentIntentID,
                clientSecret: stripeInfo?.clientSecret ? 'exists' : 'missing'
            });
        }
    }, [stripeInfo]);

    function checkData() {
        return true;
    }

    async function handleSubmit() {
        if (canSubmit !== "") {
            await alertOps(user, canSubmit, "Pre-submission validation failed", "handleSubmit - pre-submission validation");
            return false;
        }

        if (!isDataRehydrated || !stripeInfo.paymentIntentID || !stripeInfo.clientSecret) {
            logDrtEvent('[patient_app] Payment data not available for submission', {
                isDataRehydrated,
                paymentIntentID: stripeInfo?.paymentIntentID,
                clientSecret: stripeInfo?.clientSecret ? 'exists' : 'missing'
            });
            return false;
        }

        const data = formatDataNoPhotos(user, insurance, stripeInfo);
        data.insurance.cardFront = cardFront;
        data.insurance.cardBack = cardBack;

        try {
            let res = await callBackendApiPost("/api/v1/submit", data);

            if (!res.ok) {
                let errorType = null;
                if (res.status === 403) {
                    errorType = "payment";
                    setCanSubmit("pay");
                }
                else if (res.status === 400) {
                    errorType = "incorrect or missing fields";
                    setCanSubmit("fields");
                }
                else {
                    errorType = "server error";
                    setCanSubmit("error");
                }
                
                await alertOps(user, errorType, `handleSubmit - API response status ${res.status}`, "Submission of intake form");
                return false;
            }

            const responseData = await res.json();
            dispatch(userReset());
            dispatch(insuranceReset());
            dispatch(stripeReset());
            setCanSubmit("");
            return true;
        } catch (error) {
            logDrtEvent("[patient_app] Error submitting data: " + error);
            setCanSubmit("error");
            
            await alertOps(user, "unknown error", error.message, "Submission of intake form");
            return false;
        }
    }

    return [canSubmit, handleSubmit];
};
